import { useQuery, useReactiveVar } from '@apollo/client';
import { IconButton, Separator, Stack, Text } from '@fluentui/react';
import { ReferenceCodeList } from 'common/graphql/__generated__/ReferenceCodesList';
import { formatDropdownOptions } from 'common/utils';
import { PurchaseOrderCommonData } from 'purchaseOrder/view/__generated__/PurchaseOrderCommonData';
import React, { useEffect, useRef } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { setUserDefaults } from 'utility/cache/ui';
import { showPurchaseOrderModal } from '../..';
import { PurchaseOrderDataType } from '../../..';
import { PurchaseOrderItem_purchaseOrderItem } from '../../../__generated__/PurchaseOrderItem';
import { PurchaseOrderItemFormProps } from '../interface';
import { getDefaultDistribution } from '../utils';
import { ColumnsHeader } from './ColumnHeader';
import { DistributionRowTotal } from './Footer';
import { FormField } from './FormField';
import { getColumns } from './columns.data';
import { useStyles } from './index.styles';

import COMMON_DATA from '../../../../../PurchaseOrderCommonData.graphql';
import REFERENCE_CODE from 'common/graphql/ReferenceCodesList.graphql';

interface AccountingProps {
  purchaseOrder: PurchaseOrderDataType | undefined;
  purchaseOrderItem: PurchaseOrderItem_purchaseOrderItem | null | undefined;
  businessUnitId: string | null;
  isSupplierViewModal?: boolean;
}

export const Accounting: React.FC<AccountingProps> = ({
  purchaseOrderItem,
  purchaseOrder,
  businessUnitId,
  isSupplierViewModal,
}) => {
  const userDefaults = useReactiveVar(setUserDefaults);
  const { columnArray } = getColumns(userDefaults);
  const poModaReactiveProps = useReactiveVar(showPurchaseOrderModal);
  const renderRef = useRef<boolean>(true);
  const { setValue, reset, watch } =
    useFormContext<PurchaseOrderItemFormProps>();
  const { fields, remove, append } = useFieldArray({
    name: 'purchaseOrderItemDistribution',
  });
  const { data: commonData } = useQuery<PurchaseOrderCommonData>(COMMON_DATA, {
    fetchPolicy: 'cache-first',
  });

  const { data: referenceCodes } = useQuery<ReferenceCodeList>(REFERENCE_CODE, {
    fetchPolicy: 'cache-first',
  });
  const dropDownValues = {
    productionIncentivesOptions: formatDropdownOptions(
      commonData?.productionIncentives?.nodes,
      {
        getKey: (item) => item.id,
        getText: (item) => item._fullDescription!,
        includeAll: false,
      }
    ),
    tax1099T4Options: formatDropdownOptions(commonData?.tax1099T4Types?.nodes, {
      getKey: (item) => item.id,
      getText: (item) => item.tax1099T4Type!,
      includeAll: false,
    }),
  };
  const styles = useStyles();
  const watchInvoiceDistributions = watch('purchaseOrderItemDistribution');
  const lastItem = watchInvoiceDistributions?.[fields.length - 1];

  const isValueExist = lastItem
    ? Object.entries(lastItem!).reduce((prev, [key, value]) => {
        if (
          (key === 'referenceCode6' &&
            value === userDefaults?.referenceCode6) ||
          (key === 'referenceCode7' &&
            value === userDefaults?.referenceCode7) ||
          (key === 'projectReference' &&
            value === userDefaults?.projectReference) ||
          (key === 'setReference' && value === userDefaults?.setReference) ||
          (key === 'accountReference' && value === userDefaults?.accountCode) ||
          (key === '_accountName' &&
            value === userDefaults?.lookupAccounts?.lookupName) ||
          (key === 'allocationPercent' &&
            value === '100' &&
            fields.length === 1)
        )
          return prev;
        const isValue = value !== null && value !== undefined;
        return isValue || prev;
      }, false)
    : false;

  useEffect(() => {
    if (isValueExist && !renderRef.current) {
      const values = getDefaultDistribution(userDefaults);
      append(values, { shouldFocus: false });
    }
    renderRef.current = false;
  }, [append, isValueExist, userDefaults]);

  // useEffect(() => {
  //   if (fields.length === 1) {
  //     setValue(`purchaseOrderItemDistribution.0.allocationPercent`, '100', {
  //       shouldDirty: true,
  //       shouldTouch: false,
  //     });
  //   }
  // }, [fields.length]);

  const refCodesOptions = {
    refCode1Options: formatDropdownOptions(
      referenceCodes?.reference1Codes?.nodes,
      {
        getKey: (item) => item.referenceCode,
        getText: (item) => item.name!,
        includeAll: false,
      }
    ),
    refCode2Options: formatDropdownOptions(
      referenceCodes?.reference2Codes?.nodes,
      {
        getKey: (item) => item.referenceCode,
        getText: (item) => item.name!,
        includeAll: false,
      }
    ),
    refCode3Options: formatDropdownOptions(
      referenceCodes?.reference3Codes?.nodes,
      {
        getKey: (item) => item.referenceCode,
        getText: (item) => item.name!,
        includeAll: false,
      }
    ),
    refCode4Options: formatDropdownOptions(
      referenceCodes?.reference4Codes?.nodes,
      {
        getKey: (item) => item.referenceCode,
        getText: (item) => item.name!,
        includeAll: false,
      }
    ),
  };

  const { _isUpdatable, _isDistributionUpdateAllowed } = {
    ...purchaseOrder?.purchaseOrder,
  };
  const isRowUpdatable = isSupplierViewModal
    ? false
    : _isUpdatable
    ? true
    : _isDistributionUpdateAllowed;

  useEffect(() => {
    if (!poModaReactiveProps.isNew) reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Stack tokens={{ padding: '0px 0px 100px 0px' }}>
      <Separator />
      <Stack tokens={{ padding: '20px 0px 0px 20px' }}>
        <Text variant="xLarge">Accounting</Text>
      </Stack>
      <Stack className={styles.scrollContainer}>
        <Stack tokens={{ padding: '20px 0px 40px' }}>
          <Stack horizontal style={{ overflowX: 'clip' }}>
            <Stack tokens={{ padding: '52px 0px', childrenGap: 3 }}>
              {fields.map((_, index) => {
                const isLastItem = fields.length - 1 !== index;
                return (
                  <Stack key={index} style={{ height: 33, width: 40 }}>
                    {isLastItem && (
                      <IconButton
                        disabled={!isRowUpdatable||isSupplierViewModal}
                        iconProps={{ iconName: 'Blocked2Solid' }}
                        ariaLabel="delete"
                        onClick={() => remove(index)}
                        style={{ color: 'red' }}
                      />
                    )}
                  </Stack>
                );
              })}
            </Stack>
            <Stack style={{ padding: '0px 0px 40px 0px' }}>
              <ColumnsHeader columnsArray={columnArray} />
              {fields.map((field, index) => {
                const baseField = `purchaseOrderItemDistribution.${index}`;
                return (
                  <Stack key={index} horizontal tokens={{ childrenGap: 10 }}>
                    {columnArray.map((value, key) => {
                      return (
                        <FormField
                          index={index}
                          _isUpdatable={_isUpdatable}
                          _isDistributionUpdateAllowed={
                            _isDistributionUpdateAllowed
                          }
                          isSupplierViewModal={isSupplierViewModal}
                          onSetAllocationDefault={() => {
                            setValue(
                              `purchaseOrderItemDistribution.0.allocationPercent`,
                              null
                            );
                          }}
                          businessUnitId={businessUnitId}
                          rowLength={fields.length}
                          dropdownValues={dropDownValues}
                          baseField={baseField}
                          columnData={value}
                          key={field.id + key}
                          item={watchInvoiceDistributions?.[index]}
                          refCodesOptions={refCodesOptions}
                          purchaseOrder={purchaseOrder}
                          purchaseOrderItem={purchaseOrderItem}
                          onSelectAccount={(data) => {
                            setValue(
                              `purchaseOrderItemDistribution.${index}._accountName`,
                              data.lookupName
                            );
                            if (!!data.projectAccount) {
                              setValue(
                                `purchaseOrderItemDistribution.${index}.projectReference`,
                                data.projectAccount
                              );
                            }
                            if (!!data.setAccount) {
                              setValue(
                                `purchaseOrderItemDistribution.${index}.setReference`,
                                data.setAccount
                              );
                            }
                          }}
                        />
                      );
                    })}
                  </Stack>
                );
              })}
              <DistributionRowTotal columnsArray={columnArray} />
            </Stack>
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
};
